import { DefaultValue } from 'recoil';
import type { AtomEffect } from 'recoil';
import { BroadcastChannel } from 'broadcast-channel';
import debounce from 'lodash.debounce';
import mitt from 'mitt';
import type { Emitter } from 'mitt';
import { nanoid } from 'nanoid';
import equals from 'ramda/es/equals';
import { activeWindowListener } from '../modules/activeWindow';
import localForage from 'localforage';
import { logger } from '../modules/logger';
import { datadogRum } from '@datadog/browser-rum';

const LOCAL_STORAGE_PERSISTER_PREFIX = 'localStoragePersisterEffect_';
const LEGACY_STALE_AFTER_MS = 1000 * 60 * 60 * 24 * 30;
const LOCAL_FORAGE_PRUNE_INTERVAL_MS = 1000 * 60;

const LEGACY_CASE_SCOPED_LOCAL_STORAGE_PREFIXES = [
  'cadAnnotationsVisible',
  'caseReviewedStatuses',
  'hasRecordedForCaseAtom',
  'layoutsForAllCasesState',
  'scroll-link-mode',
  'thumbnailBarVisibile',
  'viewportDisplayConfiguration',
  'viewportsConfigurations',
  'windowLevelDisplay',
].map((key) => `${LOCAL_STORAGE_PERSISTER_PREFIX}${key}`);
const DISPOSABLE_VIEWER_LOCAL_STORAGE_PREFIXES = [
  ...LEGACY_CASE_SCOPED_LOCAL_STORAGE_PREFIXES,
  `${LOCAL_STORAGE_PERSISTER_PREFIX}precachedItems`,
];

export type LocalStorageTelemetry = {
  keyCount: number;
  persistedKeyCount: number;
  estimatedBytes: number;
};

let localStorageWritesBlocked = false;
let quotaFailureReported = false;
const lastLocalForagePrune = new Map<string, number>();

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getLegacyUpdatedAt(value: unknown): number | null {
  if (!isRecord(value)) return null;
  return typeof value.updated === 'number' ? value.updated : null;
}

function estimateStorageEntryBytes(key: string, value: string): number {
  // DOMStrings are UTF-16. This is an estimate, but is stable enough for telemetry and pruning.
  return (key.length + value.length) * 2;
}

export function getLocalStorageTelemetry(): LocalStorageTelemetry {
  let keyCount = 0;
  let persistedKeyCount = 0;
  let estimatedBytes = 0;

  try {
    keyCount = localStorage.length;
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key == null) continue;
      const value = localStorage.getItem(key) ?? '';
      estimatedBytes += estimateStorageEntryBytes(key, value);
      if (key.startsWith(LOCAL_STORAGE_PERSISTER_PREFIX)) persistedKeyCount += 1;
    }
  } catch (error: unknown) {
    logger.warn('Failed to measure local storage usage', { error });
  }

  return {
    keyCount,
    persistedKeyCount,
    estimatedBytes,
  };
}

/**
 * Anytime the atom state is updated sends a message to the other
 * atom instances on different windows and synchronizes its state.
 *
 * # Options
 *
 * > Only use these on local development, as they can themselves affect the
 * > overall performance of the synchronization process.
 *
 * ## FPS Reporter
 *
 * Since we don't have a real game loop to measure the fps, this actually measures the number of
 * messages received within a second. It means you need to spam many scroll events in order to
 * saturate the counter to the actual upper limit supported by the environment.
 *
 * - fpsReporter: boolean, if true, will log the fps in the developer tools console
 *
 * ## Delay Reporter
 *
 * This is a simple way to measure the delay between the time the message is sent and the time
 * it is received.
 *
 * - delayReporter: boolean, if true, will log the delay in the developer tools console
 * - delayThreshold: number, if fpsReporter is true, defines the threshold above which the delay will be logged
 *
 * ## Merge Function
 *
 * There are times where multiple pages might be simultaneously updating the Recoil state
 * with different values. In these cases, we can provide a merge function to provide a unified result.
 *
 * -- mergeFn: How to combine old and new values. Defaults to returning the new value unchanged.
 */

type Conductor = 'viewer' | 'reporter' | 'worklist';
export const CONDUCTORS: Partial<Record<Conductor, Conductor>> = {
  viewer: 'viewer',
  reporter: 'reporter',
  worklist: 'worklist',
};

type Options<T> = {
  fpsReporter?: boolean;
  delayReporter?: boolean;
  delayThreshold?: number;
  unidirectional?: boolean;
  mergeFn?: (oldValue: Nullable<T>, newValue: T) => T;
  conductor?: Conductor;
  debugLog?: boolean;
};

export const broadcastChannelSynchronizerEffect =
  <T>(userOptions?: Options<T> | null): AtomEffect<T> =>
  ({ node: { key }, setSelf, onSet }) => {
    const options = {
      fpsReporter: false,
      delayReporter: false,
      delayThreshold: 1000 / 20,
      unidirectional: false,
      conductor: null,
      debugLog: false,
      mergeFn: (oldValue: Nullable<T>, newValue: T) => newValue,
      ...userOptions,
    } as const;

    const bc = new BroadcastChannel(`broadcastChannelSynchronizerEffect_${key}`);

    const id = nanoid();
    let previousFrameTime = options.fpsReporter || options.delayReporter ? Date.now() : 0;
    let framesCount = 0;
    let value: T | undefined; // This will track the atoms current JSONified value

    // Listen to atom changes from other windows
    bc.onmessage = ({ type, origin, payload, now: then }) => {
      if (origin === id) {
        return;
      }

      if (type === 'set') {
        if (typeof payload !== 'string') return;

        const now = options.fpsReporter || options.delayReporter ? Date.now() : 0;

        if (options.fpsReporter) {
          if (previousFrameTime < now - 1000) {
            console.warn(
              `BroadcastChannelSynchronizerEffect: Atom with key "${key}" is updating at ${framesCount} FPS`
            );
            previousFrameTime = now;
            framesCount = 0;
          } else {
            framesCount += 1;
          }
        }

        if (options.delayReporter) {
          const delay = now - then;
          if (delay > options.delayThreshold) {
            console.warn(
              `BroadcastChannelSynchronizerEffect: Atom with key "${key}" was updated ${delay}ms later.`
            );
          }
        }

        // Recoil specifically ignores atom value changes from onSet() if they look like they were set via
        // a setSelf() call from the same atom effect. Depending on the batch, this can cause onSet() to not be called
        // when expected. Its best to only invoke setSelf() when it is actually updating the value of the atom.
        const payloadAsJSON = JSON.parse(payload);
        if (!equals(value, payloadAsJSON)) {
          const newValue = options.mergeFn(value ?? null, payloadAsJSON);
          if (options.debugLog) {
            logger.info(`[broadcastChannelSynchronizerEffect] Received sync for "${key}"`, {
              previous: value,
              next: newValue,
            });
          }
          value = newValue;
          setSelf(newValue);
        }
      } else if (type === 'ready') {
        if (options.unidirectional && !activeWindowListener.isWindowActive) return;

        if (options.conductor != null && !window.location.href.includes(options.conductor)) return;

        bc.postMessage({
          type: 'set',
          origin: id,
          payload: JSON.stringify(value),
          now: Date.now(),
        });
      }
    };

    // The logic below is called anytime the atom state is set
    onSet((newValue, oldValue) => {
      // Recoil delivers a DefaultValue instance to onSet when reset() is called on atoms with
      // selector-based or no defaults. We skip broadcasting these. Callers that need cross-window
      // reset should use set(atom, concreteDefault) instead of reset().
      if (newValue instanceof DefaultValue) return;
      if (options.unidirectional && !activeWindowListener.isWindowActive) return;
      if (options.conductor != null && !window.location.href.includes(options.conductor)) return;

      const now = options.fpsReporter || options.delayReporter ? Date.now() : 0;
      if (options.fpsReporter) {
        if (previousFrameTime < now - 1000) {
          console.warn(
            `BroadcastChannelSynchronizerEffect: Atom with key "${key}" is updating at ${framesCount} FPS`
          );
          previousFrameTime = now;
          framesCount = 0;
        } else {
          framesCount += 1;
        }
      }

      if (options.debugLog) {
        logger.info(`[broadcastChannelSynchronizerEffect] Sending sync for "${key}"`, {
          previous: oldValue,
          next: newValue,
        });
      }
      value = newValue;
      bc.postMessage({
        type: 'set',
        origin: id,
        payload: JSON.stringify(newValue),
        now: Date.now(),
      });
    });

    bc.postMessage({ type: 'ready', origin: id, now: Date.now() });

    // Close the BroadcastChannel when the atom is destroyed
    return () => {
      bc.close();
    };
  };

type LocalStorageSynchronizerEffectOptions = {
  // Only use integers, always increment by 1, useful if schema changes in non-backawrd compatible way
  version: number;
};

export const makeStoragePersisterEffectKey = (key: string, version: number = 1): string =>
  `${LOCAL_STORAGE_PERSISTER_PREFIX}${key}_v${version}`;

export const localStorageSynchronizerEffect =
  <T>({ version }: LocalStorageSynchronizerEffectOptions = { version: 1 }): AtomEffect<T> =>
  ({ node: { key }, setSelf }) => {
    const storageKey = makeStoragePersisterEffectKey(key, version);

    const handleStorage = (event: StorageEvent) => {
      if (
        (event.storageArea != null && event.storageArea !== window.localStorage) ||
        event.key !== storageKey
      ) {
        return;
      }

      if (event.newValue == null) {
        setSelf(new DefaultValue());
        return;
      }

      try {
        setSelf(JSON.parse(event.newValue));
      } catch (err: unknown) {
        logger.warn('Failed to parse localStorage-synchronized atom value', {
          key,
          storageKey,
          error: err,
        });
      }
    };

    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  };

function isDisposableViewerLocalStorageKey(key: string): boolean {
  return DISPOSABLE_VIEWER_LOCAL_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix));
}

function isLegacyCaseScopedLocalStorageKey(key: string): boolean {
  return LEGACY_CASE_SCOPED_LOCAL_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix));
}

function removeDisposableViewerLocalStorage(): number {
  const keys = Object.keys(localStorage).filter(isDisposableViewerLocalStorageKey);
  keys.forEach((key) => localStorage.removeItem(key));
  return keys.length;
}

// Remove obsolete case-scoped entries and retain the existing stale-value cleanup for small,
// singleton preferences. This is one pass at startup and does not parse the disposable entries.
export function cleanupStaleLocalStoragePersisterObjects() {
  const keys = Object.keys(localStorage).filter((key) =>
    key.startsWith(LOCAL_STORAGE_PERSISTER_PREFIX)
  );

  keys.forEach((key) => {
    try {
      if (isLegacyCaseScopedLocalStorageKey(key)) {
        localStorage.removeItem(key);
        return;
      }

      const raw = localStorage.getItem(key);
      if (raw == null) return;
      const updatedAt = getLegacyUpdatedAt(JSON.parse(raw));

      if (updatedAt != null && updatedAt < Date.now() - LEGACY_STALE_AFTER_MS) {
        localStorage.removeItem(key);
      }
    } catch (error: unknown) {
      logger.warn('Failed to cleanup stale Local Storage Persister object', error);
    }
  });
}
cleanupStaleLocalStoragePersisterObjects();

function isQuotaExceededError(error: unknown): boolean {
  return (
    isRecord(error) &&
    (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED')
  );
}

function handleLocalStorageQuotaFailure(failingKey: string): void {
  const before = getLocalStorageTelemetry();
  localStorageWritesBlocked = true;
  const prunedKeys = removeDisposableViewerLocalStorage();
  const after = getLocalStorageTelemetry();

  if (quotaFailureReported) return;
  quotaFailureReported = true;

  const context = {
    family: failingKey.split('__')[0],
    prunedKeys,
    before,
    after,
  };
  logger.warn('Local storage quota exceeded; writes disabled for this session', context);
  datadogRum.addAction('workspace.sys.local_storage_quota_failure', context);
}

export function acknowledgeLocalStorageReset(): void {
  localStorageWritesBlocked = false;
  quotaFailureReported = false;
}

/**
 * Legacy, prefer `localForagePersisterEffect` instead for new atoms
 *
 * This effect persist the atom state in localStorage but does not synchronize it
 * if you need to synchronize the state, use the broadcastChannelSynchronizerEffect
 * in combination with this one.
 * The persisted state is read from localStorage on every page load and is written to
 * localStorage on every atom state change with a debounce to avoid too many write accesses.
 */
export const localStoragePersisterEffect =
  <T>({ version }: LocalStorageSynchronizerEffectOptions = { version: 1 }): AtomEffect<T> =>
  ({ node: { key }, setSelf, onSet }) => {
    const storageKey = makeStoragePersisterEffectKey(key, version);

    // Remove the previous version, we assume we always increment versions by 1
    const previousStorageKey = makeStoragePersisterEffectKey(key, version - 1);
    localStorage.removeItem(previousStorageKey);

    const savedValue = window.localStorage.getItem(storageKey);

    if (savedValue != null) {
      // We do try...catch because in case the local storage has some invalid content
      // we don't want to crash the app but simply discard the data.
      try {
        setSelf(JSON.parse(savedValue));
      } catch (err: unknown) {
        logger.warn('Failed to restore local storage persisted atom', {
          key,
          error: err,
        });
      }
    }

    const debouncedSetHandler = debounce(
      (newValue: T, _: unknown, isReset: boolean) => {
        if (isReset || newValue === undefined) {
          try {
            if (localStorage.getItem(storageKey) != null) {
              localStorage.removeItem(storageKey);
            }
          } catch (error: unknown) {
            logger.warn('Failed to remove local storage persisted atom', {
              key,
              error,
            });
          }
          return;
        }

        if (localStorageWritesBlocked) return;

        let serializedValue: string;
        try {
          serializedValue = JSON.stringify(newValue);
        } catch (error: unknown) {
          logger.warn('Failed to serialize local storage persisted atom', {
            key,
            error,
          });
          return;
        }

        if (localStorage.getItem(storageKey) === serializedValue) return;

        try {
          localStorage.setItem(storageKey, serializedValue);
        } catch (error: unknown) {
          if (isQuotaExceededError(error)) {
            handleLocalStorageQuotaFailure(key);
            return;
          }

          logger.warn('Failed to write local storage persisted atom', {
            key,
            error,
          });
        }
      },
      1000,
      { leading: true }
    );

    // The logic below is called anytime the atom state is set
    onSet(debouncedSetHandler);

    // Cancel the trailing debounced write when the atom is destroyed; otherwise it
    // can fire up to 1s after teardown and touch `window`/`localStorage` against a
    // dead environment, surfacing as an unhandled error. (VX-3970)
    return () => {
      debouncedSetHandler.cancel();
    };
  };

type LocalForageStoredValue<T> = {
  value: T;
  updatedAt?: number;
};

type LocalForagePersisterEffectOptions<T> = {
  // Only use integers, always increment by 1, useful if schema changes in non-backawrd compatible way
  version?: number;
  // Atom families can opt into deterministic, least-recently-written retention.
  maxKeys?: number;
  transformBeforeSave?: (value: T) => T;
};

function getLocalForageFamilyPrefix(key: string): string {
  return `${LOCAL_STORAGE_PERSISTER_PREFIX}${getPersistedAtomFamily(key)}__`;
}

function getPersistedAtomFamily(key: string): string {
  return key.split('__')[0];
}

async function pruneLocalForageFamilyIfDue(prefix: string, maxKeys: number): Promise<void> {
  const lastPrunedAt = lastLocalForagePrune.get(prefix) ?? 0;
  if (lastPrunedAt > Date.now() - LOCAL_FORAGE_PRUNE_INTERVAL_MS) return;
  lastLocalForagePrune.set(prefix, Date.now());

  const keys = (await localForage.keys()).filter((key) => key.startsWith(prefix));
  if (keys.length <= maxKeys) return;

  const candidates = await Promise.all(
    keys.map(async (key) => {
      const stored = await localForage.getItem<LocalForageStoredValue<unknown>>(key);
      return { key, updatedAt: stored?.updatedAt ?? 0 };
    })
  );
  candidates.sort(
    (left, right) => left.updatedAt - right.updatedAt || left.key.localeCompare(right.key)
  );

  await Promise.all(
    candidates.slice(0, keys.length - maxKeys).map(({ key }) => localForage.removeItem(key))
  );
}

// This effect persist the atom state in localForage but does not synchronize it
// if you need to synchronize the state, use the broadcastChannelSynchronizerEffect
// in combination with this one.
// The persisted state is read from localForage on every page load and is written to
// localForage on every atom state change with a debounce to avoid too many write accesses.
export const localForagePersisterEffect =
  <T>({
    version = 1,
    maxKeys,
    transformBeforeSave,
  }: LocalForagePersisterEffectOptions<T> = {}): AtomEffect<T> =>
  ({ node: { key }, setSelf, onSet }) => {
    const storageKey = makeStoragePersisterEffectKey(key, version);
    const familyPrefix = maxKeys == null ? null : getLocalForageFamilyPrefix(key);
    const family = getPersistedAtomFamily(key);

    if (familyPrefix != null) {
      void pruneLocalForageFamilyIfDue(familyPrefix, maxKeys ?? 0).catch((error: unknown) => {
        logger.warn('Failed to prune persisted atom family', { family, error });
      });
    }

    // Remove the previous version, we assume we always increment versions by 1
    if (version > 1) {
      const previousStorageKey = makeStoragePersisterEffectKey(key, version - 1);
      void localForage.removeItem(previousStorageKey).catch((error: unknown) => {
        logger.warn('Failed to remove previous persisted atom version', { family, error });
      });
    }

    // Tracks whether the atom has been torn down so the async read below can't
    // hydrate a dead atom after teardown (which surfaces as an unhandled error). (VX-3970)
    let isDisposed = false;
    let wasSetLocally = false;

    // IndexedDB hydration must not participate in rendering. Suspending here causes every
    // atom-family member to add an async render boundary and can leave the viewer hidden while
    // a case is opening. Ignore a stale read if the atom changed before hydration completed.
    void localForage
      .getItem<LocalForageStoredValue<T>>(storageKey)
      .then((storedValue) => {
        if (!isDisposed && !wasSetLocally && storedValue != null) setSelf(storedValue.value);
      })
      .catch((error: unknown) => {
        logger.warn('Failed to restore persisted atom', { family, error });
      });

    const debouncedSetHandler = debounce(
      (newValue: T, _: unknown, isReset: boolean) => {
        if (isReset || newValue === undefined) {
          void localForage.removeItem(storageKey).catch((error: unknown) => {
            logger.warn('Failed to remove persisted atom', { family, error });
          });
        } else {
          // We need to wrap the value in an object because localForage will
          // return `null` if the key is not found, and Recoil will interpret
          // `null` as the actual value, by wrapping it in an object we can
          // differentiate between `null` and "not present".
          const value = transformBeforeSave?.(newValue) ?? newValue;
          void localForage
            .setItem(storageKey, { value, updatedAt: Date.now() })
            .then(() =>
              familyPrefix == null
                ? undefined
                : pruneLocalForageFamilyIfDue(familyPrefix, maxKeys ?? 0)
            )
            .catch((error: unknown) => {
              logger.warn('Failed to persist atom', { family, error });
            });
        }
      },
      1000,
      { leading: true }
    );

    // The logic below is called anytime the atom state is set
    onSet((...args) => {
      wasSetLocally = true;
      debouncedSetHandler(...args);
    });

    // Cancel the trailing debounced write and flag disposal when the atom is
    // destroyed; otherwise the debounce can fire post-teardown and the pending
    // localForage read can hydrate a dead atom. (VX-3970)
    return () => {
      isDisposed = true;
      debouncedSetHandler.cancel();
    };
  };

/**
 * Takes a ref-line object and synchronizes it with the atom state.
 * Useful to access the atom from outside the React lifecycle.
 *
 * @deprecated don't use it! It's here for legacy compatibility.
 */
// @ts-expect-error [EN-7967] - TS2315 - Type 'Emitter' is not generic.
export const copyToRefEmitter: Emitter<string, unknown> = mitt();
export const copyToRefEffect =
  <T>(ref: { current: T }): AtomEffect<T> =>
  ({ node: { key }, setSelf, onSet }) => {
    // @ts-expect-error [EN-10263] - TS7006 - Parameter 'newValue' implicitly has an 'any' type.
    copyToRefEmitter.on(key, (newValue) => {
      setSelf(newValue);
      ref.current = newValue;
    });
    onSet((newValue) => {
      ref.current = newValue;
    });
  };
